import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../../../environments/environment';
import { Area } from '../../constants/area';
import { ReportTreeNodeResponse, CoreReportResponse, CoreReportTypeResponse, CoreReportVersionResponse, CreateCoreReportResponse, CoreReportSourceResponse, ReportCategoryResponse } from './models/responses';
import { PostReportCategoryRequest } from './models/requests';



@Injectable()
export class ReportsService {

    constructor(private httpClient: HttpClient) { }

    /**
     * Get the complete hierarchical report tree
     * @returns Observable<ReportTreeNodeResponse[]>
     */
    getReportTree(divisionId?: number, loadAll?: boolean): Observable<ReportTreeNodeResponse[]> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/tree?divisionId=${divisionId}&loadAll=${loadAll}`;
        return this.httpClient.get<ReportTreeNodeResponse[]>(url);
    }
    /**
     * Get a report by its ID
     * @param id Report ID
     * @returns Observable<CoreReportResponse>
     */
    getReportById(id: number): Observable<CoreReportResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/${id}`;
        return this.httpClient.get<CoreReportResponse>(url);
    }

    /**
     * Get a report by its identifier
     * @param reportIdentifier Report identifier string
     * @returns Observable<CoreReportResponse>
     */
    getReportByIdentifier(reportIdentifier: string): Observable<CoreReportResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/identifier/${reportIdentifier}`;
        return this.httpClient.get<CoreReportResponse>(url);
    }

    /**
     * Get report type details
     * @param reportTypeId Report type ID
     * @returns Observable<CoreReportTypeResponse>
     */
    getReportType(reportTypeId: number): Observable<CoreReportTypeResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/type/${reportTypeId}`;
        return this.httpClient.get<CoreReportTypeResponse>(url);
    }

    /**
     * Get all report types
     */
    getReportTypes(divisionId: number): Observable<CoreReportTypeResponse[]> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/types?divisionId=${divisionId}`;
        return this.httpClient.get<CoreReportTypeResponse[]>(url);
    }

    /**
     * Get a specific report version
     * @param reportId Report ID
     * @param versionId Version ID
     * @returns Observable<CoreReportVersionResponse>
     */
    getReportVersion(reportId: number, versionId: number): Observable<CoreReportVersionResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/${reportId}/version/${versionId}`;
        return this.httpClient.get<CoreReportVersionResponse>(url);
    }

    /**
     * Create a new report
     * @param report Report creation data
     * @returns Observable<CoreReportResponse>
     */
    createReport(report: CreateCoreReportResponse): Observable<CoreReportResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports`;
        return this.httpClient.post<CoreReportResponse>(url, report);
    }

    /**
     * Add a new version to an existing report
     * @param reportVersion Report version data
     * @returns Observable<CoreReportResponse>
     */
    addReportVersion(reportVersion: CreateCoreReportResponse): Observable<CoreReportResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/version`;
        return this.httpClient.post<CoreReportResponse>(url, reportVersion);
    }

    /**
     * Ensure the given report type is a favorite for the current user.
     * Backend will create or reactivate the favorite as needed.
     */
    toggleReportTypeFavorite(reportTypeId: number): Observable<any> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/type/${reportTypeId}/favorite`;
        return this.httpClient.patch<any>(url, {});
    }

    /**
     * Get roles assigned to a report type
     */
    getReportTypeRoles(reportTypeId: number): Observable<any[]> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/type/${reportTypeId}/roles`;
        return this.httpClient.get<any[]>(url);
    }

    /**
     * Add a role to a report type
     */
    addReportTypeRole(reportTypeId: number, roleId: number): Observable<any> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/type/${reportTypeId}/roles/${roleId}`;
        return this.httpClient.post<any>(url, {});
    }

    /**
     * Remove a role from a report type
     */
    removeReportTypeRole(reportTypeId: number, roleId: number): Observable<any> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/type/${reportTypeId}/roles/${roleId}`;
        return this.httpClient.delete<any>(url);
    }

    getReportSources(): Observable<CoreReportSourceResponse[]> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/sources`;
        return this.httpClient.get<CoreReportSourceResponse[]>(url);
    }


    createReportType(reportType: CoreReportTypeResponse): Observable<CoreReportTypeResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports`;
        return this.httpClient.post<CoreReportTypeResponse>(url, reportType);
    }

    setReportCategory(reportCategory: PostReportCategoryRequest): Observable<ReportCategoryResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/category`;
        return this.httpClient.post<ReportCategoryResponse>(url, reportCategory);
    }

    toggleReportCategoryIsActive(categoryId: number): Observable<ReportCategoryResponse> {
        const url = `${environment.dotNetApiUrl}/${Area.core}/reports/category/${categoryId}/active`;
        return this.httpClient.patch<ReportCategoryResponse>(url, {});
    }
}



// WEBPACK FOOTER //
// ./src/app/core/components/reports/reports.service.ts